import { Box, useTheme } from "@mui/material";
import { FoodsCheckbox, FoodsRadioList, FoodsSelect } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import {
    findOptionByCode,
    findOptionByValue,
    findOptionsByCodes,
    findOptionsByValues,
} from "src/utils/dataListOptions";
import { LocalizedFoodsTextInput } from "src/components/QuestionnaireForm/FormProperty/Field/LocalizedFoodsTextInput";

type Props = {
    value: string;
    codes?: string[] | null;
    options?: DataListOption[];
    type: string;
    unit?: string;
    locale: string;
    precision?: number;
    disabled?: boolean;
    maxWidth?: string;
    label?: string;
    onChange: (value: string) => void;
    onOptionsChange: (options: DataListOption[] | DataListOption | null) => void;
    onBlur?: () => void;
};

export const ActionParameterInput = (props: Props) => {
    const {
        value,
        codes,
        options = [],
        type,
        unit,
        locale,
        precision,
        disabled = false,
        maxWidth,
        label,
        onChange,
        onOptionsChange,
        onBlur,
    } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const sortedOptions = [...options].sort((a, b) => a.position - b.position);
    const selected = codes?.length ? findOptionByCode(options, codes[0]) : findOptionByValue(options, value);

    if (type === "select") {
        return (
            <FoodsSelect
                selectedOption={selected ?? null}
                options={sortedOptions}
                onChange={onOptionsChange}
                getId={(option: DataListOption) => option.id}
                getName={(option: DataListOption) => option.label}
                disabled={disabled}
                BoxProps={{ width: "100%", role: "group", "aria-label": label }}
                textOptions={{
                    placeholder: label ?? "",
                    searchPlaceholder: "",
                    emptyOptionsText: "",
                    defaultValueText: undefined,
                    noResult: "",
                }}
            />
        );
    }

    if (type === "radio") {
        return (
            <FoodsRadioList
                values={sortedOptions}
                selectedItem={selected}
                onChange={onOptionsChange}
                getId={(option: DataListOption) => option.id}
                getName={(option: DataListOption) => option.label}
                FormControlProps={{ sx: { flexDirection: "column" }, "aria-label": label }}
                RadioProps={{ disabled }}
            />
        );
    }

    if (type === "checkbox") {
        return (
            <FoodsCheckbox
                values={sortedOptions}
                selectedItems={
                    codes?.length ?
                        findOptionsByCodes(options, codes)
                    :   findOptionsByValues(options, value ? value.split("|") : [])
                }
                onChange={onOptionsChange}
                getId={(option: DataListOption) => option.id}
                getName={(option: DataListOption) => option.label}
                CheckboxProps={{ disabled }}
            />
        );
    }

    return (
        <Box sx={{ width: "100%", minWidth: theme.spacing(12), maxWidth }}>
            <LocalizedFoodsTextInput
                value={value}
                onChange={onChange}
                unitTitle={unit}
                locale={locale}
                type={type === "number" ? "number" : "text"}
                maxFractionDigits={precision}
                enableLocalization
                disabled={disabled}
                ExtraInputProps={{
                    placeholder: t("enter_a_value", "Enter a value"),
                    onBlur,
                    inputProps: { "aria-label": label },
                }}
            />
        </Box>
    );
};
